import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { AnchorHTMLAttributes } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import {
  clearNavigatingPath,
  getNavigatingPath,
  setNavigatingPath,
} from '@/utils/navigationControl';
import { buildResolvedPath, ensureLocale } from '@/utils/urlUtils';

import { useLocale } from '@/providers/LocaleProvider';

type LinkWithLocaleProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & {
    external?: boolean;
  };

const LinkWithLocale: React.FC<LinkWithLocaleProps> = ({
  children,
  href,
  onClick,
  ...rest
}) => {
  const locale = useLocale();

  const localizedHref = useMemo(
    () => ensureLocale(href, locale),
    [href, locale],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const targetHref =
        typeof localizedHref === 'string'
          ? localizedHref
          : buildResolvedPath(
              localizedHref.pathname ?? '',
              localizedHref.query,
            ) ?? '';

      if (!href || targetHref === '') {
        onClick?.(e);
        return;
      }

      if (getNavigatingPath() === targetHref) {
        e.preventDefault();
        return;
      }

      setNavigatingPath(targetHref);
      onClick?.(e);
    },
    [localizedHref, onClick, href],
  );

  useEffect(() => {
    const handlePopstate = () => {
      const currentPath = window.location.pathname + window.location.search;
      if (getNavigatingPath() === currentPath) {
        clearNavigatingPath();
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  return (
    <Link
      aria-disabled={!!getNavigatingPath()}
      data-redirecting={!!getNavigatingPath()}
      href={localizedHref}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default memo(LinkWithLocale);
